import type { StudioSystemType } from 'types/studio/items'
import { parseIntegrationJson } from 'util/misc'
import { getRoofName } from '../nativeMounting/utils'
import {
  ensureVanDerValkPitchedCladdingOptionsForType,
  getVanDerValkPitchedCladdingOptionsFromSystem,
  isVanDerValkPitchedCladdingType,
} from './applyVanDerValkPitchedCladdingOptions'
import {
  applySelectedVanDerValkSuitability,
  applyVanDerValkSuitabilityResultToSystem,
  clearVanDerValkSuitabilityIntegration,
} from './applyVanDerValkSuitabilityResultToSystem'
import { getVanDerValkPitchedRoofStructureIssue } from './getVanDerValkPitchedRoofStructureIssue'
import {
  areVanDerValkSuitabilityModuleSpecsEqual,
  getVanDerValkSuitabilityPayload,
  isVanDerValkCachedSuitabilityContextCurrent,
} from './getVanDerValkSuitabilityPayload'
import { pickDefaultVanDerValkSuitabilityRow } from './pickDefaultVanDerValkSuitabilityRow'
import { getVanDerValkPitchedCladdingForRoofType } from './pitchedCladding'
import {
  buildPitchedCladdingOptionsCacheKey,
  buildSuitabilityCladdingOptions,
  getCorrugatedTypeFromPitchedCladdingOptions,
  getStandingSeamTypeFromPitchedCladdingOptions,
  isPitchedCladdingOptionsComplete,
  type VanDerValkPitchedCladdingType,
} from './pitchedCladdingFieldConfig'
import { postVanDerValkSuitability } from './postVanDerValkSuitability'
import {
  pitchedRoofHookRowsMissingScrewType,
  VANDERVALK_PITCHED_SUITABILITY_GROUPING_VERSION,
} from './suitabilityOptions'
import type { VanDerValkSuitabilityConfig, VanDerValkSuitabilityModuleSpecs } from './types'

export type VanDerValkSuitabilityFetchListener = {
  onStart?: () => void
  onComplete?: () => void
  onError?: (message: string) => void
}

export type VanDerValkSuitabilityFetchOptions = {
  allowEdit?: boolean
  /** Pick the first suitable configuration when none is selected (mounting on-apply flow). */
  autoSelectDefault?: boolean
  listener?: VanDerValkSuitabilityFetchListener
}

type VanDerValkSuitabilityFetchContext = {
  fetchKey: string
  shouldFetch: boolean
  isPitched: boolean
  claddingType: string | null
  pitchedCladdingType: VanDerValkPitchedCladdingType | null
  standingSeamType: string | null
  pitchedCladdingOptionsKey: string | null
  pitchedCladdingOptions: ReturnType<typeof getVanDerValkPitchedCladdingOptionsFromSystem>
  storedSuitabilityModuleSpecs: VanDerValkSuitabilityModuleSpecs | null | undefined
  rawSuitabilityResult: unknown
  storedSuitabilityCladdingType: string | null | undefined
  storedSuitabilitySeamType: string | null | undefined
  storedSuitabilityCladdingOptionsKey: string | null | undefined
  storedSuitabilityGroupingVersion: number | null | undefined
  pitchedSuitabilityRowsStale: boolean
}

function readVanDerValkSuitabilityIntegration(system: StudioSystemType) {
  return (
    parseIntegrationJson(system.integration_json) as {
      vanDerValk?: {
        suitabilityResult?: unknown
        selectedSuitability?: VanDerValkSuitabilityConfig | null
        suitabilityModuleSpecs?: VanDerValkSuitabilityModuleSpecs | null
        suitabilityCladdingType?: string | null
        suitabilitySeamType?: string | null
        suitabilityCladdingOptionsKey?: string | null
        suitabilityGroupingVersion?: number
      }
    } | null
  )?.vanDerValk
}

function buildVanDerValkSuitabilityFetchContext(system: StudioSystemType): VanDerValkSuitabilityFetchContext | null {
  const pre = getVanDerValkSuitabilityPayload(system)
  if (!pre.ok) {
    return null
  }

  const isPitched = system.mounting_type === 'onRoof'
  const roofTypeName = getRoofName(window.projectForm?.getState?.()?.values?.roof_type) ?? null
  const claddingType = isPitched ? getVanDerValkPitchedCladdingForRoofType(roofTypeName) : null
  const pitchedCladdingType =
    claddingType && isVanDerValkPitchedCladdingType(claddingType)
      ? (claddingType as VanDerValkPitchedCladdingType)
      : null
  const isStandingSeam = pitchedCladdingType === 'STANDING_SEAM'
  const isCorrugated = pitchedCladdingType === 'CORRUGATED'
  const pitchedCladdingOptions = isPitched ? getVanDerValkPitchedCladdingOptionsFromSystem(system) : null
  const standingSeamType = isStandingSeam ? getStandingSeamTypeFromPitchedCladdingOptions(pitchedCladdingOptions) : null
  const corrugatedType = isCorrugated ? getCorrugatedTypeFromPitchedCladdingOptions(pitchedCladdingOptions) : null
  const roofStructureReady = !isPitched || getVanDerValkPitchedRoofStructureIssue(system) == null
  const claddingReady =
    !isPitched ||
    (roofStructureReady &&
      claddingType != null &&
      (!isStandingSeam || standingSeamType != null) &&
      (!isCorrugated || corrugatedType != null))
  const pitchedCladdingOptionsKey =
    isPitched && pitchedCladdingType
      ? buildPitchedCladdingOptionsCacheKey(pitchedCladdingType, pitchedCladdingOptions)
      : null

  const fetchKey = `ok:${JSON.stringify(pre.moduleSpecs)}${
    isPitched
      ? `:${claddingType ?? ''}${isStandingSeam ? `:${standingSeamType ?? ''}` : ''}${
          isCorrugated ? `:${corrugatedType ?? ''}` : ''
        }:${pitchedCladdingOptionsKey ?? 'cladding-incomplete'}`
      : ''
  }`

  const vdv = readVanDerValkSuitabilityIntegration(system)
  const rawSuitabilityResult = vdv?.suitabilityResult
  const resultArray = Array.isArray(rawSuitabilityResult) ? (rawSuitabilityResult as VanDerValkSuitabilityConfig[]) : []
  const pitchedSuitabilityRowsStale =
    isPitched &&
    resultArray.length > 0 &&
    (pitchedRoofHookRowsMissingScrewType(resultArray) ||
      vdv?.suitabilityGroupingVersion !== VANDERVALK_PITCHED_SUITABILITY_GROUPING_VERSION)

  const cacheFresh =
    vdv?.suitabilityModuleSpecs != null &&
    areVanDerValkSuitabilityModuleSpecsEqual(vdv.suitabilityModuleSpecs, pre.moduleSpecs) &&
    rawSuitabilityResult != null &&
    !pitchedSuitabilityRowsStale &&
    isVanDerValkCachedSuitabilityContextCurrent(system, {
      suitabilityCladdingType: vdv.suitabilityCladdingType,
      suitabilitySeamType: vdv.suitabilitySeamType,
      suitabilityCladdingOptionsKey: vdv.suitabilityCladdingOptionsKey,
      suitabilityGroupingVersion: vdv.suitabilityGroupingVersion,
    })

  return {
    fetchKey,
    shouldFetch: claddingReady && !cacheFresh,
    isPitched,
    claddingType,
    pitchedCladdingType,
    standingSeamType,
    pitchedCladdingOptionsKey,
    pitchedCladdingOptions,
    storedSuitabilityModuleSpecs: vdv?.suitabilityModuleSpecs,
    rawSuitabilityResult,
    storedSuitabilityCladdingType: vdv?.suitabilityCladdingType,
    storedSuitabilitySeamType: vdv?.suitabilitySeamType,
    storedSuitabilityCladdingOptionsKey: vdv?.suitabilityCladdingOptionsKey,
    storedSuitabilityGroupingVersion: vdv?.suitabilityGroupingVersion,
    pitchedSuitabilityRowsStale,
  }
}

let inFlightFetchKey: string | null = null
let inFlightPromise: Promise<void> | null = null

async function runVanDerValkSuitabilityFetch(
  system: StudioSystemType,
  ctx: VanDerValkSuitabilityFetchContext,
  options?: VanDerValkSuitabilityFetchOptions
): Promise<void> {
  options?.listener?.onStart?.()

  try {
    if (ctx.isPitched && ctx.pitchedCladdingType) {
      ensureVanDerValkPitchedCladdingOptionsForType(system, ctx.pitchedCladdingType)
    }

    const pre = getVanDerValkSuitabilityPayload(system)
    if (!pre.ok) {
      return
    }

    const claddingTypeAtRun = ctx.claddingType
    const seamTypeAtRun = ctx.standingSeamType
    const claddingTypeForOpts = ctx.pitchedCladdingType
    // Re-read after ensure — ctx.pitchedCladdingOptions can be stale (e.g. missing roof-synced corrugatedType).
    const optionsAtRun = claddingTypeForOpts
      ? getVanDerValkPitchedCladdingOptionsFromSystem(system)
      : ctx.pitchedCladdingOptions
    const corrugatedTypeAtRun =
      claddingTypeAtRun === 'CORRUGATED' ? getCorrugatedTypeFromPitchedCladdingOptions(optionsAtRun) : null
    const rawSubstructure = optionsAtRun?.substructureMaterial
    const substructureMaterialAtRun =
      claddingTypeAtRun === 'CORRUGATED'
        ? rawSubstructure === 'WOOD' || rawSubstructure === 'STEEL'
          ? rawSubstructure
          : 'WOOD'
        : null
    const rawCladdingPitch = optionsAtRun?.claddingPitch
    const parsedCladdingPitch =
      typeof rawCladdingPitch === 'number' ? rawCladdingPitch : Number(String(rawCladdingPitch ?? '').trim())
    const claddingPitchAtRun =
      claddingTypeAtRun === 'CORRUGATED' && corrugatedTypeAtRun === 'STEEL'
        ? Number.isFinite(parsedCladdingPitch) && parsedCladdingPitch >= 75 && parsedCladdingPitch <= 1500
          ? parsedCladdingPitch
          : 170
        : null
    const rawCladdingThickness = optionsAtRun?.claddingThickness
    const parsedCladdingThickness =
      typeof rawCladdingThickness === 'number'
        ? rawCladdingThickness
        : Number(String(rawCladdingThickness ?? '').trim())
    const claddingThicknessAtRun =
      claddingTypeAtRun === 'CORRUGATED' && corrugatedTypeAtRun === 'STEEL'
        ? Number.isFinite(parsedCladdingThickness) && parsedCladdingThickness > 0
          ? parsedCladdingThickness
          : 0.55
        : null
    const claddingOptionsAtRun =
      claddingTypeForOpts && optionsAtRun && isPitchedCladdingOptionsComplete(claddingTypeForOpts, optionsAtRun)
        ? buildSuitabilityCladdingOptions(claddingTypeForOpts, optionsAtRun)
        : undefined
    const claddingOptionsKeyAtRun =
      claddingTypeForOpts != null
        ? buildPitchedCladdingOptionsCacheKey(claddingTypeForOpts, optionsAtRun)
        : ctx.pitchedCladdingOptionsKey

    const res = await postVanDerValkSuitability(pre.payload, {
      mountingType: system.mounting_type,
      claddingType: claddingTypeAtRun ?? undefined,
      seamType: claddingTypeAtRun === 'STANDING_SEAM' ? (seamTypeAtRun ?? undefined) : undefined,
      corrugatedType: corrugatedTypeAtRun ?? undefined,
      substructureMaterial: substructureMaterialAtRun ?? undefined,
      claddingPitch: claddingPitchAtRun ?? undefined,
      claddingThickness: claddingThicknessAtRun ?? undefined,
      claddingOptions: claddingOptionsAtRun,
    })

    if (!res.ok) {
      if (options?.allowEdit !== false) {
        clearVanDerValkSuitabilityIntegration(system)
      }
      if (res.error === 'parse') {
        options?.listener?.onError?.('Unable to run suitability check.')
      } else {
        const msg = res.message?.includes('missing project')
          ? 'Project context is missing. Reload the page and try again.'
          : res.message || 'Unable to run suitability check.'
        options?.listener?.onError?.(msg)
      }
      return
    }

    applyVanDerValkSuitabilityResultToSystem(system, {
      suitabilityResult: res.data,
      suitabilityModuleSpecs: pre.moduleSpecs,
      suitabilityCladdingType: claddingTypeAtRun,
      suitabilitySeamType: claddingTypeAtRun === 'STANDING_SEAM' ? seamTypeAtRun : null,
      suitabilityCladdingOptionsKey: claddingOptionsKeyAtRun,
    })

    if (options?.autoSelectDefault) {
      const vdv = readVanDerValkSuitabilityIntegration(system)
      if (vdv?.selectedSuitability == null && res.data.length > 0) {
        const defaultRow = pickDefaultVanDerValkSuitabilityRow(system, res.data)
        if (defaultRow) {
          applySelectedVanDerValkSuitability(system, defaultRow)
        }
      }
    }
  } finally {
    options?.listener?.onComplete?.()
  }
}

/** Deduped background suitability POST — safe to call from mounting on-apply and picker mount. */
export function requestVanDerValkSuitabilityFetchIfNeeded(
  system: StudioSystemType,
  options?: VanDerValkSuitabilityFetchOptions
): void {
  if (options?.allowEdit === false) {
    return
  }
  if (!system || system.mounting !== 'vanDerValk') {
    return
  }

  const ctx = buildVanDerValkSuitabilityFetchContext(system)
  if (!ctx?.shouldFetch) {
    return
  }

  if (inFlightFetchKey === ctx.fetchKey && inFlightPromise) {
    return
  }

  inFlightFetchKey = ctx.fetchKey
  inFlightPromise = runVanDerValkSuitabilityFetch(system, ctx, options).finally(() => {
    if (inFlightFetchKey === ctx.fetchKey) {
      inFlightFetchKey = null
      inFlightPromise = null
    }
  })
}
